import { addMonths, addWeeks, addYears } from 'date-fns';
import localStorageAvailable from 'src/utils/localStorageAvailable';

export const IGNORE_NPS_KEY_PREFIX = 'ignoreNPS';
const LEGACY_IGNORE_NPS_KEY = 'ignoreNPS';

type IgnoreNpsRecord = {
  value: true;
  expiresAt: number;
  /** How many times this user has dismissed the NPS popover on this device. */
  ignoreCount: number;
  /** When the dismiss was recorded (ms). Used to invalidate ignore after a newer email send. */
  ignoredAt?: number;
};

export function getIgnoreNpsKey(userUuid: string): string {
  return `${IGNORE_NPS_KEY_PREFIX}:${userUuid}`;
}

function getExpiresAt(ignoreCount: number, from = new Date()): number {
  if (ignoreCount >= 4) return addYears(from, 1).getTime();
  if (ignoreCount === 3) return addMonths(from, 6).getTime();
  if (ignoreCount === 2) return addMonths(from, 1).getTime();
  return addWeeks(from, 1).getTime();
}

function isValidRecord(record: unknown): record is IgnoreNpsRecord {
  if (!record || typeof record !== 'object') return false;

  const candidate = record as IgnoreNpsRecord;
  return (
    candidate.value === true &&
    typeof candidate.expiresAt === 'number' &&
    typeof candidate.ignoreCount === 'number' &&
    candidate.ignoreCount >= 1 &&
    (candidate.ignoredAt === undefined || typeof candidate.ignoredAt === 'number')
  );
}

function readRecord(userUuid: string): IgnoreNpsRecord | null {
  const raw = localStorage.getItem(getIgnoreNpsKey(userUuid));
  if (!raw) return null;

  try {
    const record = JSON.parse(raw) as unknown;
    if (isValidRecord(record)) return record;
  } catch {
    // Invalid payload — fall through and clear.
  }

  localStorage.removeItem(getIgnoreNpsKey(userUuid));
  return null;
}

/**
 * Persist an NPS dismiss preference for this user.
 * Duration escalates from the prior token's ignoreCount (including expired tokens):
 * 1st → 1 week, 2nd → 1 month, 3rd → 6 months, 4+ → 1 year.
 * Does not touch Salesforce nps_last_send_date or nps_opt_out.
 */
export function setIgnoreNps(userUuid: string): void {
  if (!localStorageAvailable() || !userUuid) return;

  const existing = readRecord(userUuid);
  const ignoreCount = (existing?.ignoreCount ?? 0) + 1;
  const now = Date.now();

  const record: IgnoreNpsRecord = {
    value: true,
    expiresAt: getExpiresAt(ignoreCount, new Date(now)),
    ignoreCount,
    ignoredAt: now
  };

  localStorage.setItem(getIgnoreNpsKey(userUuid), JSON.stringify(record));
  // Drop the pre-UUID global key if present.
  localStorage.removeItem(LEGACY_IGNORE_NPS_KEY);
}

/**
 * True when this user's ignoreNPS token is set, has not expired, and was recorded
 * on/after the current Salesforce send (so a new email re-shows the banner).
 * Keeps expired tokens for escalation.
 */
export function shouldIgnoreNps(userUuid: string, npsLastSendDate?: Date | null): boolean {
  if (!localStorageAvailable() || !userUuid) return false;

  const record = readRecord(userUuid);
  if (!record) return false;

  if (Date.now() >= record.expiresAt) return false;

  // New email send after a prior dismiss → show again.
  if (npsLastSendDate != null) {
    const sendAt = npsLastSendDate.getTime();
    // Legacy records without ignoredAt can't prove they dismissed after this send.
    if (typeof record.ignoredAt !== 'number' || record.ignoredAt < sendAt) {
      return false;
    }
  }

  return true;
}
